import { useEffect, useState } from "react";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { toast } from "sonner";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { LockKeyhole, Trophy, Loader2, CheckCircle2, UserCheck, Mail, Eye, EyeOff } from "lucide-react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { getInviteMeta, redeemParentInvite } from "@/lib/api/parent-invites.functions";
import { supabase } from "@/integrations/supabase/client";

export const Route = createFileRoute("/convite/$token")({
  head: () => ({
    meta: [
      { title: "Ativar acesso — FutBux" },
      { name: "description", content: "Ative o acesso do responsável ao portal do CT." },
      { property: "og:title", content: "Ativar acesso — FutBux" },
      { property: "og:description", content: "Ative o acesso do responsável ao portal do CT." },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary" },
    ],
  }),
  component: InvitePage,
});

type Meta = Awaited<ReturnType<typeof getInviteMeta>>;

function InvitePage() {
  const { token } = Route.useParams();
  const navigate = useNavigate();
  const getMeta = useServerFn(getInviteMeta);
  const redeem = useServerFn(redeemParentInvite);

  const [loading, setLoading] = useState(true);
  const [meta, setMeta] = useState<Meta | null>(null);
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [cpf, setCpf] = useState("");
  const [birthDate, setBirthDate] = useState("");
  const [password, setPassword] = useState("");
  const [password2, setPassword2] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [mode, setMode] = useState<"signup" | "login">("signup");
  const [showPw, setShowPw] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const r = await getMeta({ data: { token } });
        setMeta(r as Meta);
        // Já logado? Não mostrar formulário: vincular (se possível) e ir para o portal padrão.
        const { data: sess } = await supabase.auth.getSession();
        if (sess.session) {
          try {
            await redeem({ data: { token, name: null, phone: null, cpf: null, birthDate: null } });
          } catch {
            /* já vinculado ou convite consumido — segue para o portal */
          }
          navigate({ to: "/dashboard" as any });
          return;
        }
      } catch (e: any) {
        toast.error(e?.message ?? "Erro ao carregar convite");
      } finally {
        setLoading(false);
      }
    })();
  }, [token]);

  // Se o usuário voltou pelo link de confirmação de e-mail, abrir a tela de login
  useEffect(() => {
    if (typeof window === "undefined") return;
    const hash = window.location.hash || "";
    if (hash.includes("type=signup") || hash.includes("access_token")) {
      setMode("login");
      toast.success("E-mail confirmado! Faça login para ativar o acesso.");
      history.replaceState(null, "", window.location.pathname);
    }
  }, []);

  async function doLogin(e: React.FormEvent) {
    e.preventDefault();
    if (!email.trim() || !password) {
      toast.error("Informe e-mail e senha.");
      return;
    }
    setSubmitting(true);
    try {
      const si = await supabase.auth.signInWithPassword({ email: email.trim(), password });
      if (si.error) throw new Error(si.error.message);
      const r = await redeem({
        data: { token, name: null, phone: null, cpf: null, birthDate: null },
      });
      if ((r as any)?.ok) {
        setDone(true);
        toast.success("Acesso ativado com sucesso!");
        setTimeout(() => navigate({ to: "/dashboard" as any }), 900);
      }
    } catch (err: any) {
      toast.error(err?.message ?? "Não foi possível entrar.");
    } finally {
      setSubmitting(false);
    }
  }


  const isTargeted = (meta as any)?.isTargeted;
  const target = (meta as any)?.target as
    | null
    | {
        parent_id: string;
        parent_name: string;
        cpf_masked: string | null;
        already_activated: boolean;
        students: { id: string; name: string }[];
      };

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!isTargeted && name.trim().length < 2) {
      toast.error("Informe seu nome completo.");
      return;
    }
    if (!email.trim() || !password || password !== password2) {
      toast.error("Confirme e-mail e senhas iguais.");
      return;
    }
    if (password.length < 6) {
      toast.error("Senha precisa ter pelo menos 6 caracteres.");
      return;
    }
    setConfirmOpen(true);
  }

  async function submit() {
    if (!isTargeted && name.trim().length < 2) {
      toast.error("Informe seu nome completo.");
      return;
    }
    if (!email.trim() || !password || password !== password2) {
      toast.error("Confirme e-mail e senhas iguais.");
      return;
    }
    if (password.length < 6) {
      toast.error("Senha precisa ter pelo menos 6 caracteres.");
      return;
    }
    setSubmitting(true);
    try {
      // 1) Cria a conta no Auth (ou tenta login se já existir)
      let signedIn = false;
      const su = await supabase.auth.signUp({
        email: email.trim(),
        password,
        options: {
          emailRedirectTo: `${window.location.origin}/convite/${token}`,
          data: { name: target?.parent_name ?? (name.trim() || undefined), role: "parent_student" },
        },
      });
      if (su.error) {
        // Se o e-mail já existe, tenta login com a senha informada
        const si = await supabase.auth.signInWithPassword({ email: email.trim(), password });
        if (si.error) throw new Error(su.error.message || si.error.message);
        signedIn = !!si.data.session;
      } else {
        signedIn = !!su.data.session;
      }
      if (!signedIn) {
        toast.success("Confirme seu e-mail e volte a este link para fazer login.");
        setMode("login");
        return;
      }


      // 2) Vincula a ficha
      const r = await redeem({
        data: {
          token,
          name: isTargeted ? null : name.trim(),
          phone: isTargeted ? null : phone,
          cpf: isTargeted ? null : cpf,
          birthDate: isTargeted ? null : birthDate || null,
        },
      });
      if ((r as any)?.ok) {
        setDone(true);
        toast.success("Acesso ativado com sucesso!");
        setTimeout(() => navigate({ to: "/dashboard" as any }), 900);
      }
    } catch (e: any) {
      toast.error(e?.message ?? "Erro ao ativar acesso");
    } finally {
      setSubmitting(false);
    }
  }

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin" />
      </div>
    );
  }

  if (!meta || (meta as any).valid === false) {
    return (
      <Shell>
        <Card>
          <CardContent className="p-6 space-y-4 text-center">
            <div className="mx-auto h-12 w-12 rounded-full bg-muted flex items-center justify-center">
              <LockKeyhole className="h-6 w-6 text-muted-foreground" />
            </div>
            <div className="space-y-2">
              <h2 className="text-xl font-semibold">
                {target?.already_activated ? "Este acesso já foi ativado" : "Convite indisponível"}
              </h2>
              <p className="text-sm text-muted-foreground">
                {target?.already_activated
                  ? "A ficha deste responsável já está vinculada a um usuário. Faça login normalmente."
                  : "Este link não é válido, foi revogado ou expirou. Peça um novo ao seu Centro de Treinamento."}
              </p>
            </div>
            <Button asChild className="w-full">
              <Link to="/auth">Ir para o login</Link>
            </Button>
          </CardContent>
        </Card>
      </Shell>
    );
  }

  if (done) {
    return (
      <Shell>
        <Card>
          <CardContent className="p-6 text-center space-y-3">
            <CheckCircle2 className="h-10 w-10 text-green-600 mx-auto" />
            <h2 className="text-xl font-semibold">Acesso ativado!</h2>
            <p className="text-sm text-muted-foreground">Redirecionando para o portal…</p>
          </CardContent>
        </Card>
      </Shell>
    );
  }

  return (
    <Shell tcName={(meta as any).tc?.name}>
      <Card>
        <CardContent className="p-6 space-y-5">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center">
              <UserCheck className="h-5 w-5 text-primary" />
            </div>
            <div>
              <h2 className="text-lg font-semibold">
                {mode === "login"
                  ? "Entrar na minha conta"
                  : isTargeted
                    ? "Ative seu acesso"
                    : "Criar meu cadastro"}{" "}
                {mode === "signup" && (
                  <span className="text-green-600 font-semibold">(dados do responsável)</span>
                )}
              </h2>
              <p className="text-xs text-muted-foreground">
                {mode === "login"
                  ? "Use o e-mail já confirmado para acessar o Centro de Treinamento "
                  : isTargeted
                    ? "Ficha criada pelo Centro de Treinamento "
                    : "Você será vinculado ao Centro de Treinamento "}
                <strong>{(meta as any).tc?.name}</strong>.
              </p>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-2">
            <button
              type="button"
              onClick={() => setMode("signup")}
              className={`rounded-md py-2.5 text-sm font-bold transition border-2 ${
                mode === "signup"
                  ? "bg-emerald-600 border-emerald-700 text-white shadow"
                  : "bg-background border-emerald-600/40 text-emerald-700 hover:bg-emerald-50"
              }`}
            >
              Criar cadastro
            </button>
            <button
              type="button"
              onClick={() => setMode("login")}
              className={`rounded-md py-2.5 text-sm font-bold transition border-2 ${
                mode === "login"
                  ? "bg-red-600 border-red-700 text-white shadow"
                  : "bg-background border-red-600/40 text-red-700 hover:bg-red-50"
              }`}
            >
              Já tenho conta
            </button>
          </div>


          {target && (
            <div className="rounded-lg border p-3 space-y-1 bg-muted/30">
              <div className="text-sm">
                <span className="text-muted-foreground">Responsável: </span>
                <strong>{target.parent_name}</strong>
              </div>
              {target.cpf_masked && (
                <div className="text-xs text-muted-foreground">CPF: {target.cpf_masked}</div>
              )}
              {target.students.length > 0 && (
                <div className="text-xs">
                  <span className="text-muted-foreground">Atletas vinculados: </span>
                  {target.students.map((s) => s.name).join(", ")}
                </div>
              )}
            </div>
          )}

          {mode === "login" ? (
            <form onSubmit={doLogin} className="space-y-3">
              <div>
                <Label>E-mail</Label>
                <Input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="voce@exemplo.com"
                  required
                />
              </div>
              <div>
                <Label>Senha</Label>
                <div className="relative">
                  <Input
                    type={showPw ? "text" : "password"}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    minLength={6}
                    className="pr-10"
                  />
                  <button
                    type="button"
                    tabIndex={-1}
                    onClick={() => setShowPw((v) => !v)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                    aria-label={showPw ? "Ocultar senha" : "Mostrar senha"}
                  >
                    {showPw ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
              </div>
              <Button type="submit" disabled={submitting} className="w-full bg-red-600 hover:bg-red-700 text-white font-bold">
                {submitting && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                Entrar e ativar acesso
              </Button>
              <p className="text-[11px] text-muted-foreground text-center">
                Ainda não tem conta? Use a aba "Criar cadastro".
              </p>
            </form>
          ) : (
          <form onSubmit={handleSubmit} className="space-y-3">

            {!isTargeted && (
              <>
                <div>
                  <Label>Nome completo</Label>
                  <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Seu nome" required />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label>Telefone (WhatsApp)</Label>
                    <Input value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="(31) 99999-9999" />
                  </div>
                  <div>
                    <Label>CPF</Label>
                    <Input value={cpf} onChange={(e) => setCpf(e.target.value)} placeholder="000.000.000-00" />
                  </div>
                </div>
                <div>
                  <Label>Data de nascimento do responsável</Label>
                  <Input type="date" value={birthDate} onChange={(e) => setBirthDate(e.target.value)} />
                </div>
              </>
            )}
            <div>
              <Label>E-mail</Label>
              <Input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="voce@exemplo.com"
                required
              />
            </div>
            <div>
              <Label>Senha</Label>
              <div className="relative">
                <Input
                  type={showPw ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  minLength={6}
                  className="pr-10"
                />
                <button
                  type="button"
                  tabIndex={-1}
                  onClick={() => setShowPw((v) => !v)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                  aria-label={showPw ? "Ocultar senha" : "Mostrar senha"}
                >
                  {showPw ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>
            <div>
              <Label>Confirmar senha</Label>
              <div className="relative">
                <Input
                  type={showPw ? "text" : "password"}
                  value={password2}
                  onChange={(e) => setPassword2(e.target.value)}
                  required
                  minLength={6}
                  className="pr-10"
                />
                <button
                  type="button"
                  tabIndex={-1}
                  onClick={() => setShowPw((v) => !v)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                  aria-label={showPw ? "Ocultar senha" : "Mostrar senha"}
                >
                  {showPw ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>
            <Button type="submit" disabled={submitting} className="w-full bg-emerald-600 hover:bg-emerald-700 text-white font-bold">
              {submitting && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Ativar acesso
            </Button>
            <p className="text-[11px] text-muted-foreground text-center">
              Já tem conta com este e-mail? Digite a mesma senha para vincular ao cadastro.
            </p>
          </form>
          )}

        </CardContent>
      </Card>

      <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <Mail className="h-5 w-5 text-primary" /> Confirme antes de ativar
            </AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-2 text-left">
                <p>
                  Os dados preenchidos aqui são{" "}
                  <strong className="text-green-600">os dados do responsável</strong>, não do atleta.
                </p>
                <p>
                  Para ativar o acesso é necessário abrir o seu e-mail e confirmar o registro. Sem essa
                  confirmação o acesso não é liberado.
                </p>
                <p>
                  Depois de confirmar o acesso, você deverá preencher os dados do atleta dentro do portal.
                </p>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Voltar</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => {
                e.preventDefault();
                setConfirmOpen(false);
                void submit();
              }}
            >
              Entendi, ativar acesso
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Shell>
  );
}

function Shell({ children, tcName }: { children: React.ReactNode; tcName?: string }) {
  return (
    <div className="min-h-screen flex items-center justify-center bg-muted/40 px-4 py-8">
      <div className="w-full max-w-md space-y-4">
        <div className="flex flex-col items-center">
          <div className="h-14 w-14 rounded-full bg-primary/10 flex items-center justify-center mb-3">
            <Trophy className="h-7 w-7 text-primary" />
          </div>
          <h1 className="text-2xl font-bold">FutBux</h1>
          {tcName && <p className="text-xs text-muted-foreground">{tcName}</p>}
        </div>
        {children}
      </div>
    </div>
  );
}
